<template>
  <div class="min-h-screen custom-filter-page">
    <!-- Header -->
    <header v-if="showHeader" class="bg-white shadow-sm border-b">
      <div class="mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <img
              src="https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png"
              alt="Selectair"
              class="w-12 h-12 object-contain"
            />
            <div>
              <h1 class="text-2xl font-bold text-gray-900">
                Selectair Vakanties
              </h1>
              <p class="text-gray-600 mt-1">Vind jouw perfecte vakantie</p>
            </div>
          </div>

          <div class="flex items-center gap-4">
            <router-link
              v-if="showAgentFinder"
              to="/agent-finder"
              class="flex items-center gap-2 px-4 py-2 text-theme border border-theme rounded-lg hover:bg-theme hover:text-white transition-colors"
            >
              <svg
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span class="hidden sm:block">Vind reisagent</span>
            </router-link>
          </div>
        </div>
      </div>
    </header>

    <div class="mx-auto">
      <div class="flex flex-col xl:flex-row gap-8">
        <!-- Mobile Filter Toggle Button -->
        <div class="xl:hidden mb-4">
          <button
            @click="toggleMobileFilters"
            class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme text-white rounded-lg hover:bg-theme-hover transition-colors"
          >
            <svg
              class="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Bestemmingen & Filters
            <span class="bg-white bg-opacity-20 text-xs px-2 py-1 rounded-full">
              {{ activeFilterCount }}
            </span>
          </button>
        </div>

        <!-- Filters Sidebar -->
        <div class="relative">
          <!-- Mobile overlay -->
          <div
            v-if="showMobileFilters"
            class="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
            @click="closeMobileFilters"
          ></div>

          <!-- Sidebar -->
          <div
            :class="[
              'w-full xl:w-80 flex-shrink-0 transition-transform duration-300 ease-in-out',
              'xl:relative xl:transform-none xl:z-auto',
              showMobileFilters
                ? 'fixed top-0 left-0 h-full bg-white z-50 transform translate-x-0 overflow-y-auto'
                : 'xl:block hidden xl:translate-x-0 -translate-x-full',
            ]"
          >
            <!-- Mobile close button -->
            <div
              class="xl:hidden flex justify-between items-center p-4 border-b bg-gray-50"
            >
              <h3 class="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                @click="closeMobileFilters"
                class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <FilterSidebar
              :filters="filters"
              :vacations="vacations"
              @filter-change="onFilterChange"
              @clear-filters="clearAllFilters"
            />
          </div>
        </div>

        <!-- Results Area -->
        <div class="flex-1 min-w-0">
          <!-- Results Header -->
          <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div
              class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4"
            >
              <div>
                <h2 class="text-xl font-semibold text-gray-900">
                  {{ displayVacationCount }} vakanties gevonden
                </h2>
                <p class="text-gray-600 mt-1">
                  {{ activeFiltersText }}
                </p>
              </div>
              <div class="flex items-center space-x-4">
                <label class="text-gray-700">Sorteren op:</label>
                <select
                  v-model="sortBy"
                  class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-theme focus:border-transparent"
                >
                  <option value="priority">Prioriteit</option>
                  <option value="name">Naam</option>
                  <option value="newest">Nieuwste</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center h-64">
            <div
              class="animate-spin rounded-full h-12 w-12 border-b-2 border-theme"
            ></div>
          </div>

          <!-- Error State -->
          <div
            v-else-if="error"
            class="bg-red-50 border border-red-200 rounded-lg p-6 text-center"
          >
            <p class="text-red-600">
              Er is een fout opgetreden bij het laden van de vakanties.
            </p>
            <button
              @click="loadVacations"
              class="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Opnieuw proberen
            </button>
          </div>

          <!-- Results Grid -->
          <div
            v-else-if="filteredVacations.length > 0"
            class="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            <VacationCard
              v-for="vacation in paginatedVacations"
              :key="vacation.id"
              :vacation="vacation"
              @click="openVacationPreview"
            />
          </div>

          <!-- No Results -->
          <div v-else class="bg-white rounded-lg shadow-sm p-12 text-center">
            <div class="text-gray-400 mb-4">
              <svg
                class="mx-auto h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.664-2.409M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-medium text-gray-900 mb-2">
              Geen vakanties gevonden
            </h3>
            <p class="text-gray-600 mb-6">
              Probeer uw filters aan te passen om meer resultaten te vinden.
            </p>
            <button
              @click="clearAllFilters"
              class="px-6 py-3 bg-theme text-white rounded-md hover:bg-theme-hover transition-colors"
            >
              Filters wissen
            </button>
          </div>

          <!-- Pagination -->
          <div
            v-if="filteredVacations.length > itemsPerPage"
            class="mt-8 flex justify-center"
          >
            <nav class="flex items-center space-x-2">
              <button
                @click="goToPage(Math.max(1, currentPage - 1))"
                :disabled="currentPage === 1"
                class="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Vorige
              </button>
              <span
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-4 py-2 text-sm cursor-pointer rounded-md',
                  currentPage === page
                    ? 'bg-theme text-white'
                    : 'text-gray-700 hover:bg-gray-100',
                ]"
              >
                {{ page }}
              </span>
              <button
                @click="goToPage(Math.min(totalPages, currentPage + 1))"
                :disabled="currentPage === totalPages"
                class="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Volgende →
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- HTML Preview Modal -->
    <div
      v-if="showPreviewModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 modal-overlay"
      @click="closePreviewModal"
    >
      <div
        class="bg-white rounded-lg w-full h-full max-w-[1200px] max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex justify-between items-center p-3 sm:p-4">
          <div class="flex-1 min-w-0">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-900 truncate"
            >
              {{ selectedVacation?.metadata?.title || selectedVacation?.name }}
            </h3>
          </div>
          <button
            @click="closePreviewModal"
            class="ml-3 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
          >
            <svg
              class="h-8 w-8 sm:h-6 sm:w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="flex-1 overflow-hidden">
          <!-- Loading State -->
          <div
            v-if="previewLoading"
            class="flex items-center justify-center h-full"
          >
            <div class="text-center">
              <div
                class="animate-spin rounded-full h-12 w-12 border-b-2 border-theme mx-auto mb-4"
              ></div>
              <p class="text-gray-600">Preview wordt geladen...</p>
            </div>
          </div>

          <!-- Error State -->
          <div
            v-else-if="previewError"
            class="flex items-center justify-center h-full"
          >
            <div class="text-center p-8">
              <svg
                class="mx-auto h-16 w-16 text-red-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h4 class="text-lg font-medium text-gray-900 mb-2">
                Preview niet beschikbaar
              </h4>
              <p class="text-gray-600">
                Er is een fout opgetreden bij het laden van de preview.
              </p>
            </div>
          </div>

          <!-- Preview Content -->
          <iframe
            v-else-if="previewHtml"
            :srcdoc="previewHtml"
            class="w-full h-full border-0"
            frameborder="0"
            @load="onIframeLoad"
          ></iframe>
        </div>

        <!-- Modal Footer -->
        <div class="p-3 sm:p-4 border-t bg-gray-50">
          <!-- Mobile Layout -->
          <div class="sm:hidden">
            <!-- Categories -->
            <div class="mb-4">
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="category in limitedCategories.visible"
                  :key="category"
                  class="bg-theme-light text-theme text-xs px-2 py-1 rounded-full"
                >
                  {{ formatCategory(category) }}
                </span>
                <button
                  v-if="limitedCategories.hasMore"
                  @click="toggleShowAllCategories"
                  class="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full hover:bg-gray-300 transition-colors"
                >
                  {{
                    showAllCategories
                      ? "Minder"
                      : `+${limitedCategories.remaining} meer`
                  }}
                </button>
              </div>
            </div>

            <!-- Buttons -->
            <div class="flex flex-col gap-4">
              <button
                @click="closePreviewModal"
                class="w-full px-4 py-3 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors uppercase text-sm"
              >
                Sluiten
              </button>
              <button
                v-if="showCTA"
                @click="handleCTA(selectedVacation)"
                class="w-full px-6 py-3 bg-theme text-white rounded-md hover:bg-theme-hover transition-colors uppercase font-bold text-sm"
              >
                {{ shopSettings.ctaText }}
              </button>
            </div>
          </div>

          <!-- Desktop Layout -->
          <div class="hidden sm:flex justify-between items-center">
            <div class="flex flex-wrap gap-2 flex-1 mr-4">
              <span
                v-for="category in limitedCategories.visible"
                :key="category"
                class="bg-theme-light text-theme text-sm px-3 py-1.5 rounded-full"
              >
                {{ formatCategory(category) }}
              </span>
              <button
                v-if="limitedCategories.hasMore"
                @click="toggleShowAllCategories"
                class="bg-gray-200 text-gray-600 text-sm px-3 py-1.5 rounded-full hover:bg-gray-300 transition-colors"
              >
                {{
                  showAllCategories
                    ? "Minder"
                    : `+${limitedCategories.remaining} meer`
                }}
              </button>
            </div>
            <div class="flex space-x-3 flex-shrink-0">
              <button
                @click="closePreviewModal"
                class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors uppercase"
              >
                Sluiten
              </button>
              <button
                v-if="showCTA"
                @click="handleCTA(selectedVacation)"
                class="px-6 py-2 bg-theme text-white rounded-md hover:bg-theme-hover transition-colors uppercase font-bold"
              >
                {{ shopSettings.ctaText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UTDService from "@/services/UTDService";
import VacationCard from "@/components/VacationCard.vue";
import FilterSidebar from "@/components/FilterSidebar.vue";

export default {
  name: "VacationBooking",
  components: {
    VacationCard,
    FilterSidebar,
  },
  data() {
    return {
      // Loading and error states
      loading: false,
      error: false,

      // Data
      vacations: [],
      selectedVacation: null,

      // Preview modal
      showPreviewModal: false,
      previewLoading: false,
      previewError: false,
      previewHtml: "",

      // Filter state
      filters: {
        searchQuery: "",
        selectedRegion: "",
        selectedCountry: "",
        selectedTravelStyles: [],
        selectedDuration: "",
        selectedBudget: "",
        selectedSeasons: [],
        selectedSpecialInterests: [],
      },

      lang: this.shopSettings.lang || "nl",

      // Pagination and sorting
      currentPage: 1,
      itemsPerPage: 16,
      sortBy: "priority",

      // Mobile filter state
      showMobileFilters: false,

      // Category display state
      showAllCategories: false,
      maxVisibleCategories: 3, // Max categories to show initially

      // First load state
      isFirstLoad: true,
      hiddenCurrentMonthFilter: null, // Hidden filter for current month on first load

      // Region to countries mapping for filtering
      // ONLY actual countries - no brands, themes, travel types, etc.
      regionCountries: {
        'europa': [
          // Countries
          'spanje', 'portugal', 'frankrijk', 'italië', 'griekenland', 'kroatië', 
          'turkije', 'cyprus', 'malta', 'ijsland', 'noorwegen', 'zweden', 'finland', 
          'denemarken', 'duitsland', 'oostenrijk', 'slowakije', 'nederland', 'belgië',
          'belgië ', 'engeland', 'groot-brittannië', 'schotland', 'ierland', 'polen', 
          'albanië', 'montenegro', 'estland', 'lapland',
          // Islands & regions
          'sicilië', 'ibiza', 'tenerife', 'corfu', 'kreta', 'faeröer', 'canarische eilanden',
          // Cities (popular destinations)
          'rome', 'porto', 'lissabon', 'dublin'
        ],
        'afrika': [
          // Countries
          'marokko', 'egypte', 'égypte', 'tunesië', 'kenia', 'tanzania', 
          'oeganda', 'zuid-afrika', 'botswana', 'namibië', 'zimbabwe', 'madagaskar', 
          'mauritius', 'seychellen', 'reunion', 'kaapverdië', 'senegal',
          // Islands & regions
          'zanzibar', 'sal', 'djerba'
        ],
        'azië': [
          // Countries
          'turkije', 'japan', 'china', 'india', 'sri lanka', 'thailand', 
          'vietnam', 'maleisië', 'indonesie', 'oman',
          'verenigde arabische emiraten', 'jordanië', 'zuid-korea',
          // Islands & regions
          'bali', 'malediven',
          // Cities (popular destinations)
          'tokyo', 'dubai', 'rasalkhaimah'
        ],
        'noord-amerika': [
          // Countries
          'canada', 'verenigde staten', 'usa', 'mexico', 'honduras', 'costa rica', 'panama',
          // States & regions
          'florida', 'oregon', 'westkust',
          // Islands
          "bahama's",
          // Cities/Routes
          'lasvegas', 'route66'
        ],
        'zuid-amerika': [
          // Countries
          'colombia', 'peru', 'brazilië', 'chili', 'argentinië',
          // Regions
          'patagonië',
          // Cities
          'ushuaia', ' ushuaia'
        ],
        'oceanie': [
          'australië'
        ],
        'oceanië': [
          'australië'
        ],
        'caraiben': [
          // Countries & Islands
          'cuba', 'jamaica', 'dominicaanse republiek', 'curaçao', 'curacao'
        ],
        'midden-oosten': [
          'turkije', 'dubai', 'verenigde arabische emiraten', 'oman', 'jordanië',
          'rasalkhaimah'
        ],
        'middenoosten': [
          'turkije', 'dubai', 'verenigde arabische emiraten', 'oman', 'jordanië',
          'rasalkhaimah'
        ],
        'antartica': [
          'argentinië', 'chili', 'ushuaia', ' ushuaia'
        ]
      }
    };
  },

  computed: {
    filteredVacations() {
      let filtered = this.vacations;

      // Apply hidden current month filter on first load
      if (this.isFirstLoad && this.hiddenCurrentMonthFilter) {
        filtered = filtered.filter((vacation) =>
          vacation.metadata.categories.some(
            (cat) =>
              cat.toLowerCase() === this.hiddenCurrentMonthFilter.toLowerCase()
          )
        );
      }

      // Search filter
      if (this.filters.searchQuery) {
        const query = this.filters.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (vacation) =>
            vacation.metadata.title.toLowerCase().includes(query) ||
            vacation.name.toLowerCase().includes(query) ||
            vacation.metadata.categories.some((cat) =>
              cat.toLowerCase().includes(query)
            )
        );
      }

      // Region filter (mapped to categories)
      // Also includes vacations that have countries belonging to the selected region
      if (this.filters.selectedRegion) {
        const region = this.filters.selectedRegion.toLowerCase();
        const countriesInRegion = this.regionCountries[region] || [];
        
        filtered = filtered.filter((vacation) =>
          vacation.metadata.categories.some((cat) => {
            const catLower = cat.toLowerCase();
            // Match if category is the region itself OR a country in that region
            return catLower === region || countriesInRegion.includes(catLower);
          })
        );
      }

      // Country filter (mapped to categories for now)
      if (this.filters.selectedCountry) {
        filtered = filtered.filter((vacation) =>
          vacation.metadata.categories.some((cat) =>
            cat.toLowerCase() === this.filters.selectedCountry.toLowerCase()
          )
        );
      }

      // Travel style filter (mapped to categories)
      if (this.filters.selectedTravelStyles.length > 0) {
        filtered = filtered.filter((vacation) =>
          this.filters.selectedTravelStyles.some((style) =>
            vacation.metadata.categories.some(
              (cat) => cat.toLowerCase() === style.toLowerCase()
            )
          )
        );
      }

      // Season/Month filter
      if (this.filters.selectedSeasons.length > 0) {
        filtered = filtered.filter((vacation) =>
          this.filters.selectedSeasons.some((season) =>
            vacation.metadata.categories.some(
              (cat) => cat.toLowerCase() === season.toLowerCase()
            )
          )
        );
      }

      // Special interests filter
      if (this.filters.selectedSpecialInterests.length > 0) {
        filtered = filtered.filter((vacation) =>
          this.filters.selectedSpecialInterests.some((interest) =>
            vacation.metadata.categories.some(
              (cat) => cat.toLowerCase() === interest.toLowerCase()
            )
          )
        );
      }

      // Sort results
      if (this.sortBy === "name") {
        filtered.sort((a, b) =>
          a.metadata.title.localeCompare(b.metadata.title)
        );
      } else if (this.sortBy === "newest") {
        filtered.sort((a, b) => b.id - a.id);
      } else if (this.isFirstLoad) {
        // Randomize on first load
        filtered = this.shuffleArray([...filtered]);
      }

      return filtered;
    },

    paginatedVacations() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      // remove vakanties in metadata.categories just dont include the label vakanties
      const updatedVacations = this.filteredVacations.map((vacation) => {
        return {
          ...vacation,
          metadata: {
            ...vacation.metadata,
            categories: vacation.metadata.categories.filter(
              (category) => category !== "vakanties"
            ),
          },
        };
      });
      return updatedVacations.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredVacations.length / this.itemsPerPage);
    },

    visiblePages() {
      const totalVisible = 5;
      const half = Math.floor(totalVisible / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    activeFiltersText() {
      const filters = [];
      if (this.filters.searchQuery)
        filters.push(`"${this.filters.searchQuery}"`);
      if (this.filters.selectedCountry)
        filters.push(this.filters.selectedCountry);
      if (this.filters.selectedTravelStyles.length > 0)
        filters.push(`${this.filters.selectedTravelStyles.length} reistypes`);
      if (this.filters.selectedSeasons.length > 0)
        filters.push(`${this.filters.selectedSeasons.length} maanden`);
      if (this.filters.selectedSpecialInterests.length > 0)
        filters.push(
          `${this.filters.selectedSpecialInterests.length} categorieën`
        );

      if (filters.length === 0) return "Alle vakanties";
      return `Gefilterd op: ${filters.join(", ")}`;
    },

    activeFilterCount() {
      let count = 0;

      if (this.filters.searchQuery) count++;
      if (this.filters.selectedRegion) count++;
      if (this.filters.selectedCountry) count++;
      if (this.filters.selectedTravelStyles.length > 0) count++;
      if (this.filters.selectedDuration) count++;
      if (this.filters.selectedBudget) count++;
      if (this.filters.selectedSeasons.length > 0) count++;
      if (this.filters.selectedSpecialInterests.length > 0) count++;

      return count;
    },

    displayVacationCount() {
      // On first load, show total vacation count to give impression of full catalog
      // Once user applies filters, show actual filtered count
      if (this.isFirstLoad) {
        return this.vacations.length;
      }
      return this.filteredVacations.length;
    },

    limitedCategories() {
      const categories = this.selectedVacation?.metadata?.categories || [];
      const total = categories.length;

      if (this.showAllCategories || total <= this.maxVisibleCategories) {
        return {
          visible: categories,
          hasMore: false,
          remaining: 0,
        };
      }

      return {
        visible: categories.slice(0, this.maxVisibleCategories),
        hasMore: true,
        remaining: total - this.maxVisibleCategories,
      };
    },

    showAgentFinder() {
      // Show agent finder button if URL parameter is present
      return (
        this.$route.query["show-agent-finder"] === "true" ||
        this.$route.query["agent-finder"] === "true" ||
        this.$route.query["show-agents"] === "true" ||
        this.$route.query["office"] === "true"
      );
    },

    showHeader() {
      // Hide header if URL parameter is present
      const urlHideHeader = this.$route.query["hide-header"] === "true";
      const urlNoHeader = this.$route.query["no-header"] === "true";
      const urlHeadless = this.$route.query["headless"] === "true";
      const dataNoHeader = this.shopSettings.noHeader === "true";

      return !urlHideHeader && !urlNoHeader && !urlHeadless && !dataNoHeader;
    },

    showCTA() {
      // Hide CTA buttons if URL parameter is present
      return (
        this.$route.query["hide-cta"] !== "true" &&
        this.$route.query["no-cta"] !== "true" &&
        this.$route.query["hide-buttons"] !== "true" &&
        this.$route.query["no-buttons"] !== "true"
      );
    },
  },

  watch: {
    // Reset pagination when filters change
    filters: {
      handler() {
        this.currentPage = 1;
        // Update URL when filters change (but not on first load)
        if (!this.isFirstLoad) {
          this.updateUrlFromFilters();
        }
      },
      deep: true,
    },

    // Watch for URL changes (browser back/forward)
    "$route.query": {
      handler(newQuery) {
        // Only update filters if not first load to avoid conflicts
        if (!this.isFirstLoad) {
          this.updateFiltersFromUrl();
        }
      },
      deep: true,
    },
  },

  async created() {
    await this.loadVacations();

    // Check if URL has filter parameters (means it's not a fresh first load)
    const hasUrlFilters = this.hasFilterParametersInUrl();

    if (hasUrlFilters) {
      // If URL has filters, load them and disable first load behavior
      this.isFirstLoad = false;
      this.updateFiltersFromUrl();
    } else {
      // First load: set up hidden current month filter
      this.setupFirstLoadBehavior();
    }
  },

  methods: {
    async loadVacations() {
      this.loading = true;
      this.error = false;

      try {
        this.vacations = await UTDService.getProducts(
          this.shopSettings.label,
          this.shopSettings.lang,
          this.shopSettings.siteId
        );
      } catch (error) {
        console.error("Error loading vacations:", error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    onFilterChange(filterUpdates) {
      // Any filter change means it's no longer first load
      this.isFirstLoad = false;
      this.hiddenCurrentMonthFilter = null;

      this.filters = { ...this.filters, ...filterUpdates };
      this.scrollToTop();
      // Auto-close mobile filters when a filter is applied for better UX
      if (this.showMobileFilters) {
        setTimeout(() => {
          this.closeMobileFilters();
        }, 300); // Small delay for better UX
      }
    },

    clearAllFilters() {
      // Clearing filters means it's no longer first load
      this.isFirstLoad = false;
      this.hiddenCurrentMonthFilter = null;

      this.filters = {
        searchQuery: "",
        selectedRegion: "",
        selectedCountry: "",
        selectedTravelStyles: [],
        selectedDuration: "",
        selectedBudget: "",
        selectedSeasons: [],
        selectedSpecialInterests: [],
      };
      this.currentPage = 1;
      this.scrollToTop();
    },

    async openVacationPreview(vacation) {
      let siteId = this.shopSettings.siteId;
      console.log("vacation info", vacation);
      this.selectedVacation = vacation;
      this.showPreviewModal = true;
      this.previewLoading = true;
      this.previewError = false;
      this.previewHtml = "";

      try {
        const response = await fetch(
          `https://www.uptodateconnect.com/api/v1/site-builder/sites-page-html-export?siteId=${siteId}&pageId=${vacation.pageId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const htmlContent = await response.text();

        // Get the vacation page URL from the vacation object's bloggerPageUrl
        const vacationPageUrl = vacation.bloggerPageUrl
          ? `${this.shopSettings.baseUrl}${vacation.bloggerPageUrl}`
          : `${this.shopSettings.baseUrl}/p/${vacation.pageId}`;

        console.log("Vacation Page URL:", vacationPageUrl);

        // Get vacation name for URL parameter
        const vacationName = vacation.metadata?.title || vacation.name || "";
        const encodedVacationName = encodeURIComponent(vacationName);
        const encodedVacationLink = encodeURIComponent(vacationPageUrl);

        // Replace localhost URLs with the proper domain using dynamic baseUrl
        let processedHtml = htmlContent
          .replace(
            /http:\/\/selectair-vakanties.uptodatesites.com/g,
            this.shopSettings.baseUrl
          )
          .replace(/href="\/p\//g, `href="${this.shopSettings.baseUrl}/p/`);
        // .replace(/src="\/uploads\//g, `src="${this.shopSettings.baseUrl}/uploads/`)
        // .replace(/src="\/assets\//g, `src="${this.shopSettings.baseUrl}/assets/`);

        // Add vacation name to specific links (graag-een-offerte, contact forms, etc.)
        processedHtml = processedHtml.replace(
          /href="([^"]*(?:\/)?(?:graag-een-offerte|contact)[^"]*)"(?![^"]*vakantie=)/g,
          (match, url) => {
            // Skip if vakantie parameter already exists in the URL
            if (url.includes("vakantie=")) {
              return match;
            }
            const separator = url.includes("?") ? "&" : "?";
            return `href="${url}${separator}vakantie=${encodedVacationName}"`;
          }
        );

        // Handle href attributes with vakantie-link parameter
        processedHtml = processedHtml.replace(
          /href="([^"]*(?:\/)?(?:graag-een-offerte|contact)[^"]*)"(?![^"]*vakantie-link=)/g,
          (match, url) => {
            // Skip if vakantie-link parameter already exists in the URL
            if (url.includes("vakantie-link=")) {
              return match;
            }
            const separator = url.includes("?") ? "&" : "?";
            return `href="${url}${separator}vakantie-link=${encodedVacationLink}"`;
          }
        );

        this.previewHtml = `
          <link href='https://fonts.googleapis.com/css?family=Montserrat:400,400i,600,600i' id='selected-fonts' rel='stylesheet' type='text/css'/>
          <link href='https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css' rel='stylesheet' type='text/css'/>
          <link href='https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.css' rel='stylesheet' type='text/css'/>
          <style id='style-theme-override'>body{font-weight:400;}h1{font-family:'Montserrat',serif;font-weight:null;font-size:36px;color:#000000;}h2{font-family:'Montserrat',serif;font-weight:null;font-size:30px;color:#000;}h3{font-family:'Montserrat',serif;font-weight:null;font-size:24px;color:#000000;}h4{font-family:'Montserrat',serif;font-weight:null;font-size:18px;color:#000000;}h5{font-family:'Montserrat',serif;font-weight:null;font-size:16px;color:#000000;}h6{font-family:'Montserrat',serif;font-weight:null;font-size:14px;color:#000000;}#primary-menu{background-color:#0a3209;}#sub-footer{background-color:#0a3209;}:root{--body-font-family:'Montserrat',serif;--body-font-color:#666666;--body-font-size:16px;--body-link:#e31c18;--body-link-hover:#354046;--body-line-height:1.6em;--menu-font-size:16px;--menu-icon-color:inherit;--menu-font-family:Montserrat;--menu-color:#000000;--menu-hover-link:inherit;--menu-hover-card:inherit;--menu-text-transform:normal;--footer-font-family:'Montserrat',serif;--footer-color:#FFFFFF;--footer-icon:#0a3209;--footer-font-size:16px;--footer-link:#0a3209;--footer-link-hover:#354046;--footer-line-height:1.5em;--page-title-color:#000000 --page-title-font-size:16px;--page-title-font-family:Montserrat;--page-title-line-height:1.5;--page-title-link:#0a3209;--page-title-hover:v#354046;--sub-footer-font-family:'Montserrat',serif;--sub-footer-color:#000000;--sub-footer-icon:inherit;--sub-footer-font-size:16px;--sub-footer-link:inherit;--sub-footer-link-hover:inherit;--sub-footer-line-height:1.5em;--topbar-font-size:53px;--topbar-icon-color:#0a3209;--topbar-font-family:Montserrat;--topbar-color:#000000;--container-max-width:1200px;--theme:#e31c18;--block-padding:6rem;--theme-hover:#354046;</style>
          <style>
            :root {
              --theme: ${this.shopSettings.theme};
              --block-padding: 6rem;
              --theme-hover: ${this.shopSettings.themeHover};
            }
            body {
              margin: 0;
            }
          </style> ${processedHtml}
          <script>
            var utdElementSiteUrl = '${siteId}';
            var BLOGGER_URL = '/';
          <\/script>
          <script src='https://unpkg.com/swiper@6.1.2/swiper-bundle.min.js'><\/script>
          <script src='https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js'><\/script>
          <script src='https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.multi-platform.js'><\/script>
        `;
      } catch (error) {
        console.error("Error loading preview:", error);
        this.previewError = true;
      } finally {
        this.previewLoading = false;
      }
    },

    closePreviewModal() {
      this.showPreviewModal = false;
      this.selectedVacation = null;
      this.previewHtml = "";
      this.previewError = false;
      this.showAllCategories = false; // Reset category display state
    },

    toggleShowAllCategories() {
      this.showAllCategories = !this.showAllCategories;
    },

    onIframeLoad() {
      console.log("🔥 onIframeLoad called");

      // Check if the main app is running inside an iframe
      const isInIframe = window.self !== window.top;
      console.log("🖼️ App is in iframe:", isInIframe);

      // Simple approach: just open the current vacation page on any click
      const iframe = document.querySelector("iframe");
      console.log("🖼️ Iframe found:", !!iframe);

      if (iframe && iframe.contentDocument) {
        console.log(
          "📄 Iframe contentDocument found, injecting simple click handler..."
        );

        // Check if script was already injected to prevent duplicates
        if (iframe.contentDocument.getElementById("selectair-simple-handler")) {
          console.log("⚠️ Script already injected, skipping");
          return;
        }

        const script = iframe.contentDocument.createElement("script");
        script.id = "selectair-simple-handler";
        script.textContent = `
          console.log("🚀 Smart click handler injected");
          
          // Smart: preserve original link behavior but open in new tab
          document.addEventListener('click', function(e) {
            console.log("🔥 Click detected in iframe!");
            
            // Find the closest link element
            let target = e.target;
            while (target && target.tagName !== 'A' && target.parentElement) {
              target = target.parentElement;
            }
            
            // If it's a link, handle it
            if (target && target.tagName === 'A' && target.href) {
              console.log("🔗 Link clicked:", target.href);
              
              // Prevent default behavior
              e.preventDefault();
              e.stopPropagation();
              
              // Get the original URL the user clicked
              let targetUrl = target.href;
              
              // If it's a relative URL, make it absolute
              if (!targetUrl.startsWith('http')) {
                const baseUrl = '${this.shopSettings.baseUrl}';
                if (targetUrl.startsWith('/')) {
                  targetUrl = baseUrl + targetUrl;
                } else {
                  targetUrl = baseUrl + '/' + targetUrl;
                }
              }
              
              console.log("🎯 Opening original link:", targetUrl);
              
              // Open the actual link the user clicked in new tab
              window.open(targetUrl, '_blank');
              
              return false;
            } else {
              // If not a link, just log it but don't redirect
              console.log("🖱️ Non-link click, no action taken");
            }
          }, true); // Use capture phase to catch all clicks
          
          console.log("✅ Smart click handler ready");
        `;

        iframe.contentDocument.head.appendChild(script);
        console.log("✅ Simple script injected successfully");

        // Emit event for future handling
        this.$emit("iframe-click-handler-ready", {
          vacation: this.selectedVacation,
          baseUrl: this.shopSettings.baseUrl,
        });
      } else {
        console.log("❌ Could not access iframe contentDocument");
      }

      console.log(
        "🏁 Preview loaded successfully - behavior depends on iframe context"
      );
    },

    handleCTA(vacation) {
      const ctaUrl = this.shopSettings.ctaUrl;

      // If CTA URL is provided and not just a placeholder, handle it
      if (ctaUrl && ctaUrl !== "#") {
        // Check if it's an external URL
        if (ctaUrl.startsWith("http://") || ctaUrl.startsWith("https://")) {
          // Open external URL in new tab
          window.open(ctaUrl, "_blank");
        } else if (ctaUrl.startsWith("mailto:")) {
          // Handle mailto links
          window.location.href = ctaUrl;
        } else {
          // Handle internal URLs
          window.location.href = ctaUrl;
        }

        // Close modal after CTA action
        this.closePreviewModal();
      } else {
        // Fallback behavior for when no URL is configured
        alert(
          `${this.shopSettings.ctaText} voor "${vacation.metadata.title}" - Deze functionaliteit wordt nog geïmplementeerd.`
        );
        this.closePreviewModal();
      }
    },

    // Keep the old method for backward compatibility if needed elsewhere
    bookVacation(vacation) {
      this.handleCTA(vacation);
    },

    formatCategory(category) {
      return category.charAt(0).toUpperCase() + category.slice(1);
    },

    toggleMobileFilters() {
      this.showMobileFilters = !this.showMobileFilters;
    },

    closeMobileFilters() {
      this.showMobileFilters = false;
    },

    goToPage(page) {
      this.currentPage = page;
      this.scrollToTop();
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    // First load behavior
    setupFirstLoadBehavior() {
      const currentMonth = this.getCurrentMonth();
      console.log("🎲 Setting up first load with current month:", currentMonth);

      // Check if current month has any vacations
      const currentMonthVacations = this.vacations.filter((vacation) =>
        vacation.metadata.categories.some(
          (cat) => cat.toLowerCase() === currentMonth.toLowerCase()
        )
      );

      console.log(
        `📅 Vacations for ${currentMonth}:`,
        currentMonthVacations.length
      );

      if (currentMonthVacations.length > 0) {
        this.hiddenCurrentMonthFilter = currentMonth;
        console.log("✅ Using current month filter:", currentMonth);
      } else {
        // If no vacations for current month, find a month that has vacations
        const availableMonth = this.findAvailableMonth();
        console.log(
          "🔍 No vacations for current month, using:",
          availableMonth
        );
        this.hiddenCurrentMonthFilter = availableMonth;
      }

      this.isFirstLoad = true;
    },

    getCurrentMonth() {
      const months = [
        "januari",
        "februari",
        "maart",
        "april",
        "mei",
        "juni",
        "juli",
        "augustus",
        "september",
        "oktober",
        "november",
        "december",
      ];
      const currentMonthIndex = new Date().getMonth();
      const currentMonth = months[currentMonthIndex];
      console.log(
        "📅 Current month index:",
        currentMonthIndex,
        "→",
        currentMonth
      );
      return currentMonth;
    },

    findAvailableMonth() {
      const months = [
        "januari",
        "februari",
        "maart",
        "april",
        "mei",
        "juni",
        "juli",
        "augustus",
        "september",
        "oktober",
        "november",
        "december",
      ];

      // Check all vacation categories to find available months
      const availableMonths = new Set();
      this.vacations.forEach((vacation) => {
        if (vacation.metadata && vacation.metadata.categories) {
          vacation.metadata.categories.forEach((category) => {
            const lowerCategory = category.toLowerCase();
            if (months.includes(lowerCategory)) {
              availableMonths.add(lowerCategory);
            }
          });
        }
      });

      console.log("🗓️ Available months in data:", Array.from(availableMonths));

      // Try to find current month first, then next month, then any available month
      const currentMonth = this.getCurrentMonth();
      if (availableMonths.has(currentMonth)) {
        return currentMonth;
      }

      // If current month not available, try next month
      const currentIndex = months.indexOf(currentMonth);
      const nextIndex = (currentIndex + 1) % months.length;
      const nextMonth = months[nextIndex];
      if (availableMonths.has(nextMonth)) {
        return nextMonth;
      }

      // Otherwise return first available month
      return Array.from(availableMonths)[0] || null;
    },

    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },

    // URL State Management
    hasFilterParametersInUrl() {
      const query = this.$route.query;
      const filterParams = [
        "search",
        "region",
        "country",
        "travel",
        "seasons",
        "interests",
      ];
      return filterParams.some((param) => query[param]);
    },

    updateUrlFromFilters() {
      const query = { ...this.$route.query }; // Preserve existing params like siteId, baseUrl, etc.

      // Remove old filter params
      delete query.search;
      delete query.region;
      delete query.country;
      delete query.travel;
      delete query.seasons;
      delete query.interests;

      // Add current filter values
      if (this.filters.searchQuery) {
        query.search = this.filters.searchQuery;
      }

      if (this.filters.selectedRegion) {
        query.region = this.filters.selectedRegion;
      }

      if (this.filters.selectedCountry) {
        query.country = this.filters.selectedCountry;
      }

      if (this.filters.selectedTravelStyles.length > 0) {
        query.travel = this.filters.selectedTravelStyles.join(",");
      }

      if (this.filters.selectedSeasons.length > 0) {
        query.seasons = this.filters.selectedSeasons.join(",");
      }

      if (this.filters.selectedSpecialInterests.length > 0) {
        query.interests = this.filters.selectedSpecialInterests.join(",");
      }

      // Update URL without triggering navigation
      this.$router.replace({ query }).catch(() => {
        // Ignore navigation failures (happens when query is same)
      });
    },

    updateFiltersFromUrl() {
      const query = this.$route.query;

      this.filters = {
        searchQuery: query.search || "",
        selectedRegion: query.region || "",
        selectedCountry: query.country || "",
        selectedTravelStyles: query.travel
          ? query.travel.split(",").filter(Boolean)
          : [],
        selectedDuration: "",
        selectedBudget: "",
        selectedSeasons: query.seasons
          ? query.seasons.split(",").filter(Boolean)
          : [],
        selectedSpecialInterests: query.interests
          ? query.interests.split(",").filter(Boolean)
          : [],
      };

      console.log("🔗 Updated filters from URL:", this.filters);
    },
  },
};
</script>

<style>
/* Scoped styles for Selectair widget */
/* Theme colors are inherited from :root (set via data-theme in HTML by main.js) */
.custom-filter-page {
  --selectair-theme: var(--theme);
  --selectair-theme-hover: var(--theme-hover);
  --selectair-theme-light: var(--theme-light);
  max-width: 1400px;
  margin: 0 auto;
}

/* Theme classes with scoped variables */
.custom-filter-page .bg-theme {
  background-color: var(--selectair-theme) !important;
}

.custom-filter-page .bg-theme-hover {
  background-color: var(--selectair-theme-hover) !important;
}

.custom-filter-page .bg-theme-light {
  background-color: var(--selectair-theme-light) !important;
}

.custom-filter-page .text-theme {
  color: var(--selectair-theme) !important;
}

.custom-filter-page .border-theme {
  border-color: var(--selectair-theme) !important;
}

/* Tailwind text size classes adjusted for 10px rem base */
.custom-filter-page .text-xs {
  font-size: 1.2rem !important; /* 12px */
  line-height: 1.6rem !important; /* 16px */
  font-weight: 300 !important;
}

.custom-filter-page .text-sm {
  font-size: 1.4rem !important; /* 14px */
  line-height: 2rem !important; /* 20px */
  font-weight: 300 !important;
}

.custom-filter-page .text-base {
  font-size: 1.6rem !important; /* 16px */
  line-height: 2.4rem !important; /* 24px */
}

.custom-filter-page .text-lg {
  font-size: 1.5rem !important; /* 18px */
  line-height: 2rem !important; /* 28px */
}

.custom-filter-page .text-xl {
  font-size: 2rem !important; /* 20px */
  line-height: 2.5rem !important; /* 28px */
}

.custom-filter-page .text-2xl {
  font-size: 2.4rem !important; /* 24px */
  line-height: 3.2rem !important; /* 32px */
}

.custom-filter-page .text-3xl {
  font-size: 3rem !important; /* 30px */
  line-height: 3.6rem !important; /* 36px */
}

/* Keep original text sizes for headings - don't override */
.custom-filter-page h1 {
  font-size: inherit;
}

.custom-filter-page h2 {
  font-size: inherit;
}

.custom-filter-page h3 {
  font-size: inherit;
}

/* Form elements - minimal fixes only */
.custom-filter-page input[type="text"],
.custom-filter-page input[type="email"],
.custom-filter-page input[type="tel"],
.custom-filter-page input[type="number"],
.custom-filter-page input[type="date"],
.custom-filter-page input[type="search"],
.custom-filter-page input[type="password"],
.custom-filter-page select,
.custom-filter-page textarea {
  font-size: 14px !important;
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 6px !important;
  padding: 8px 12px !important;
}

.custom-filter-page select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: right 8px center !important;
  background-repeat: no-repeat !important;
  background-size: 16px !important;
  padding-right: 40px !important;
}

/* Button styles - minimal */
.custom-filter-page button {
  font-size: 14px !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

/* Labels */
.custom-filter-page label {
  font-size: 14px !important;
  font-weight: 500 !important;
}

/* Focus states */
.custom-filter-page input:focus,
.custom-filter-page select:focus,
.custom-filter-page textarea:focus {
  outline: none !important;
  border-color: var(--selectair-theme) !important;
  box-shadow: 0 0 0 3px rgba(227, 28, 24, 0.1) !important;
}

/* Modal z-index fix */
.custom-filter-page .modal-overlay {
  z-index: 9999 !important;
}

/* Line clamp utility */
.custom-filter-page .line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Prevent conflicts with external styles */
.custom-filter-page * {
  box-sizing: border-box;
}
</style>
